import React, { useState, useEffect, useMemo } from 'react';
import { Product, SelectedOption } from '../types';
import { AllergenDisplay } from './AllergenDisplay';
import { calculateProductAllergens } from '../worker/allergens';
import { useStocksStore } from '../store/stocksStore';
import { useCartStore } from '../store/cartStore';
import { calculateDisplayPrice } from '../shared/pricing';
import ImageModal from './ImageModal';
import './ProductOptionsModal.css';

type ProductOptionDefinition = Product['options'][number];

const getChoiceNames = (option: ProductOptionDefinition, choiceIds: string[]): string[] => {
    const uniqueChoiceIds = Array.from(new Set(choiceIds.filter(Boolean)));
    return uniqueChoiceIds
        .map(choiceId => option.choices.find(choice => choice.id === choiceId)?.name)
        .filter((name): name is string => Boolean(name));
};

const createSelectedOption = (option: ProductOptionDefinition, choiceIds: string[]): SelectedOption => ({
    optionId: option.id,
    optionName: option.name,
    choiceIds,
    choiceNames: getChoiceNames(option, choiceIds)
});

const upsertSelectedOption = (
    selected: SelectedOption[],
    option: ProductOptionDefinition,
    choiceIds: string[]
): SelectedOption[] => {
    const sanitizedChoiceIds = Array.from(new Set(choiceIds.filter(Boolean)));
    const existingIndex = selected.findIndex(opt => opt.optionId === option.id);

    if (sanitizedChoiceIds.length === 0) {
        if (existingIndex === -1) {
            return selected;
        }
        return selected.filter(opt => opt.optionId !== option.id);
    }

    const nextSelectedOption = createSelectedOption(option, sanitizedChoiceIds);

    if (existingIndex === -1) {
        return [...selected, nextSelectedOption];
    }

    return selected.map((opt, index) => (index === existingIndex ? nextSelectedOption : opt));
};

interface ProductOptionsModalProps {
    product: Product;
    isOpen: boolean;
    onClose: () => void;
    onAddToCart: (selectedOptions: SelectedOption[], quantity: number) => void;
    // Edit mode props
    editMode?: boolean;
    initialSelectedOptions?: SelectedOption[];
    initialQuantity?: number;
    highZIndex?: boolean;
}

export const ProductOptionsModal: React.FC<ProductOptionsModalProps> = ({
    product,
    isOpen,
    onClose,
    onAddToCart,
    editMode = false,
    initialSelectedOptions,
    initialQuantity,
    highZIndex = false
}) => {
    const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
    const [quantity, setQuantity] = useState(1);
    const [errors, setErrors] = useState<string[]>([]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const { stocks, fetchStocks } = useStocksStore();
    const { cart } = useCartStore();

    // Fetch stocks on mount if not already loaded
    useEffect(() => {
        fetchStocks();
    }, [fetchStocks]);

    const availableStock = product.availableStock ?? null;
    const hasLimitedStock = availableStock !== null;
    const isOutOfStock = hasLimitedStock && availableStock <= 0;
    const canAddEvenIfOutOfStock = product.allowStockException ?? false;

    // Get discount percentage from cart
    const discountPercentage = cart.discount?.percentage;
    const productPriceDisplay = calculateDisplayPrice(product.price, discountPercentage);

    // Calculate allergens dynamically based on selected options
    const allergenSummary = useMemo(() => {
        return calculateProductAllergens(product, stocks || [], selectedOptions);
    }, [product, stocks, selectedOptions]);
    useEffect(() => {
        if (isOpen) {
            // In edit mode, use the provided initial values
            if (editMode && initialSelectedOptions) {
                setSelectedOptions(initialSelectedOptions);
                setQuantity(initialQuantity ?? 1);
                setErrors([]);
                return;
            }

            // Default mode: use default selections from product options
            const defaultOptions: SelectedOption[] = [];

            product.options.forEach(option => {
                const defaultChoices: string[] = [];

                // Get default selections for this option
                option.choices.forEach(choice => {
                    if (choice.defaultSelected) {
                        defaultChoices.push(choice.id);
                    }
                });

                // Add to initial options if there are defaults or if required
                if (defaultChoices.length > 0 || option.required) {
                    defaultOptions.push(createSelectedOption(option, defaultChoices));
                }
            });

            setSelectedOptions(defaultOptions);
            setQuantity(1);
            setErrors([]);
        }
    }, [isOpen, product, editMode, initialSelectedOptions, initialQuantity]);

    const handleOptionChange = (optionId: string, choiceId: string, isChecked: boolean) => {
        setSelectedOptions(prev => {
            const option = product.options.find(opt => opt.id === optionId);
            if (!option) return prev;

            const existingSelection = prev.find(opt => opt.optionId === optionId);
            let nextChoiceIds: string[] = existingSelection ? [...existingSelection.choiceIds] : [];

            if (option.type === 'multiple') {
                const choiceSet = new Set(nextChoiceIds);
                if (isChecked) {
                    choiceSet.add(choiceId);
                } else {
                    choiceSet.delete(choiceId);
                }
                nextChoiceIds = Array.from(choiceSet);
            } else {
                // For single choice (radio), allow unselecting if clicking the same choice again
                const isCurrentlySelected = nextChoiceIds.includes(choiceId);
                if (isCurrentlySelected) {
                    // Unselect if clicking the already-selected radio button
                    nextChoiceIds = [];
                } else {
                    // Select the new choice
                    nextChoiceIds = [choiceId];
                }
            }

            return upsertSelectedOption(prev, option, nextChoiceIds);
        });
    };

    const isChoiceSelected = (optionId: string, choiceId: string): boolean => {
        const selectedOption = selectedOptions.find(opt => opt.optionId === optionId);
        return selectedOption ? selectedOption.choiceIds.includes(choiceId) : false;
    };

    const calculateTotalPrice = (): number => {
        let totalPrice = productPriceDisplay.currentPrice;

        selectedOptions.forEach(selectedOption => {
            const option = product.options.find(opt => opt.id === selectedOption.optionId);
            if (option) {
                selectedOption.choiceIds.forEach(choiceId => {
                    const choice = option.choices.find(ch => ch.id === choiceId);
                    if (choice) {
                        const choiceDisplay = calculateDisplayPrice(choice.extraCost, discountPercentage);
                        totalPrice += choiceDisplay.currentPrice;
                    }
                });
            }
        });

        return totalPrice;
    };

    const validateSelection = (): boolean => {
        const newErrors: string[] = [];

        // Check all required options are selected
        product.options.forEach(option => {
            if (option.required) {
                const selectedOption = selectedOptions.find(opt => opt.optionId === option.id);
                if (!selectedOption || selectedOption.choiceIds.length === 0) {
                    newErrors.push(`Please select ${option.name}`);
                }
            }
        });

        setErrors(newErrors);
        return newErrors.length === 0;
    };

    const handleAddToCart = () => {
        if (!validateSelection()) {
            return;
        }

        if (isOutOfStock && !canAddEvenIfOutOfStock) {
            return;
        }

        const permittedQuantity = (hasLimitedStock && !canAddEvenIfOutOfStock) ? Math.min(quantity, availableStock) : quantity;
        if (permittedQuantity <= 0) {
            return;
        }

        onAddToCart(selectedOptions, permittedQuantity);
        onClose();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Real-time validation effect
    useEffect(() => {
        const newErrors: string[] = [];

        // Check all required options are selected
        product.options.forEach(option => {
            if (option.required) {
                const selectedOption = selectedOptions.find(opt => opt.optionId === option.id);
                if (!selectedOption || selectedOption.choiceIds.length === 0) {
                    newErrors.push(`Please select ${option.name}`);
                }
            }
        });

        setErrors(newErrors);
    }, [selectedOptions, product.options]);

    if (!isOpen) return null;

    return (
        <div className={`product-options-modal-backdrop${highZIndex ? ' high-z-index' : ''}`} onClick={handleBackdropClick}>
            <div className="product-options-modal">
                <div className="modal-header">
                    <h2>{editMode ? `Edit: ${product.name}` : product.name}</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    <div className="product-info">
                        <div
                            className="product-image-wrapper"
                            onClick={() => setIsImageModalOpen(true)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setIsImageModalOpen(true);
                                }
                            }}
                        >
                            <img src={product.image} alt={product.name} />
                            <div className="image-zoom-overlay">
                                <span>🔍 Click to enlarge</span>
                            </div>
                        </div>

                        {/* Campaign Badge */}
                        {product.campaignText && (
                            <div className="campaign-badge-modal">
                                {product.campaignText}
                            </div>
                        )}

                        {/* Price Display with Discount Strikethrough */}
                        {productPriceDisplay.hasDiscount && productPriceDisplay.originalPrice && (
                            <div className="price-display-modal">
                                <span className="original-price-modal">
                                    £{productPriceDisplay.originalPrice.toFixed(2)}
                                </span>
                                <span className="current-price-modal">
                                    £{productPriceDisplay.currentPrice.toFixed(2)}
                                </span>
                            </div>
                        )}

                        <p className="product-description">{product.description}</p>
                        {product.detailedDescription && (
                            <div className="product-detailed-description">
                                <p>{product.detailedDescription}</p>
                            </div>
                        )}
                    </div>

                    {[...product.options].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(option => (
                        <div key={option.id} className="option-group">
                            <h3 className="option-title">
                                {option.name}
                                {option.required && <span className="required">*</span>}
                            </h3>

                            <div className="option-choices">
                                {[...option.choices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(choice => (
                                    <label key={choice.id} className="option-choice">
                                        <input
                                            type={option.type === 'single' ? 'radio' : 'checkbox'}
                                            name={option.type === 'single' ? option.id : undefined}
                                            checked={isChoiceSelected(option.id, choice.id)}
                                            onChange={(e) => handleOptionChange(option.id, choice.id, e.target.checked)}
                                            onClick={(e) => {
                                                // For radio buttons, allow unselecting if already selected
                                                if (option.type === 'single' && isChoiceSelected(option.id, choice.id)) {
                                                    e.preventDefault();
                                                    handleOptionChange(option.id, choice.id, false);
                                                }
                                            }}
                                        />
                                        <span className="choice-label">
                                            {choice.name}
                                            {choice.extraCost > 0 && (() => {
                                                const choiceDisplay = calculateDisplayPrice(choice.extraCost, discountPercentage);
                                                return (
                                                    <span className="choice-price-display">
                                                        {choiceDisplay.hasDiscount && choiceDisplay.originalPrice && (
                                                            <span className="choice-original-price">
                                                                £{choiceDisplay.originalPrice.toFixed(2)}
                                                            </span>
                                                        )}
                                                        <span className="extra-cost">£{choiceDisplay.currentPrice.toFixed(2)}</span>
                                                    </span>
                                                );
                                            })()}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}

                    {errors.length > 0 && (
                        <div className="validation-errors">
                            {errors.map((error, index) => (
                                <p key={index} className="error-message">{error}</p>
                            ))}
                        </div>
                    )}

                    {/* Allergen information - Compact view after options */}
                    {allergenSummary.allAllergens.length > 0 && (
                        <AllergenDisplay
                            allergens={allergenSummary}
                            compact={true}
                        />
                    )}

                    <div className="quantity-selector">
                        <label>Quantity:</label>
                        <div className="quantity-controls">
                            <button
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                disabled={quantity <= 1}
                            >
                                -
                            </button>
                            <span className="quantity-display">{quantity}</span>
                            <button
                                onClick={() => setQuantity(prev => {
                                    const next = prev + 1;
                                    if (hasLimitedStock && availableStock !== null && !canAddEvenIfOutOfStock) {
                                        return Math.min(next, availableStock);
                                    }
                                    return next;
                                })}
                                disabled={hasLimitedStock && availableStock !== null && quantity >= availableStock && !canAddEvenIfOutOfStock}
                            >
                                +
                            </button>
                        </div>
                        {/* {hasLimitedStock && (
                            <p className="stock-hint">
                                {isOutOfStock ? (canAddEvenIfOutOfStock ? 'Available' : 'Currently out of stock') : `Up to ${availableStock} available${canAddEvenIfOutOfStock ? ' (can order more)' : ''}`}
                            </p>
                        )} */}
                    </div>
                </div>                <div className="modal-footer">
                    <div className="total-price">
                        Total: £{(calculateTotalPrice() * quantity).toFixed(2)}
                    </div>
                    <div className="modal-actions">
                        <button className="cancel-button" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="add-to-cart-button"
                            onClick={handleAddToCart}
                            disabled={(isOutOfStock && !canAddEvenIfOutOfStock) || errors.length > 0}
                        >
                            {isOutOfStock && !canAddEvenIfOutOfStock 
                                ? 'Out of Stock' 
                                : editMode 
                                    ? 'Update' 
                                    : 'Add to Cart'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Image Modal for enlarged view */}
            <ImageModal
                isOpen={isImageModalOpen}
                imageUrl={product.image}
                imageName={product.name}
                product={product}
                onClose={() => setIsImageModalOpen(false)}
            />
        </div>
    );
};
